import { Loading } from '@nike/frame-component-library';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';

import { activePromotionsGet } from '../../../utils/service-calls/store-data-validation';
import { ButtonBlack } from '../../reusable';

const PROMOTION_COLUMNS = [
  { key: 'promotionId', label: 'Promotion ID' },
  { key: 'name', label: 'Name' },
  { key: 'status', label: 'Status' },
  { key: 'strategy', label: 'Strategy' },
  { key: 'benefitType', label: 'Benefit Type' },
  { key: 'thresholdType', label: 'Threshold Type' },
  { key: 'applyTo', label: 'Apply To' },
  { key: 'channel', label: 'Channel' },
  { key: 'benefitTiers', label: 'Benefit Tiers' },
  { key: 'skuCount', label: 'SKU Count' },
  { key: 'authoredDate', label: 'Authored Date' },
  { key: 'modificationDate', label: 'Modification Date' },
  { key: 'effectiveLocalDateRangeStart', label: 'Effective Start' },
  { key: 'effectiveLocalDateRangeEnd', label: 'Effective End' },
];

const DATE_COLUMNS = new Set([
  'authoredDate',
  'modificationDate',
  'effectiveLocalDateRangeStart',
  'effectiveLocalDateRangeEnd',
]);

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (Array.isArray(value)) return value.length ? String(value.length) : '-';
  return String(value);
};

const formatDate = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString();
};

const normalisePromotions = (body) => {
  const promotions = body?.promotions || [];
  return promotions.map((promo, index) => ({
    ...promo,
    rowId: `${promo.promotionId || 'promo'}-${index}`,
  }));
};

const renderBenefitTiers = (tiers) => {
  if (!Array.isArray(tiers) || tiers.length === 0) return '-';
  return (
    <ul className="sdv-tier-list">
      {tiers.map((tier) => (
        <li key={`${tier.threshold}-${tier.value}`} className="sdv-tier-item">
          <span className="sdv-tier-threshold">{`Threshold: ${tier.threshold}`}</span>
          <span className="sdv-tier-value">{`Value: ${tier.value}`}</span>
        </li>
      ))}
    </ul>
  );
};

const renderPromotionCell = (row, col) => {
  if (DATE_COLUMNS.has(col.key)) {
    return formatDate(row[col.key]);
  }
  if (col.key === 'benefitTiers') {
    return renderBenefitTiers(row.benefitTiers);
  }
  return formatValue(row[col.key]);
};

const PromotionTab = ({ accessToken, selectedStore }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);

  const locationId = selectedStore?.id;
  const hasStore = Boolean(locationId);

  useEffect(() => {
    setData(null);
    setError('');
    setFetched(false);
    setLoading(false);
  }, [locationId]);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const handleFetch = useCallback(() => {
    if (!locationId) return;
    setError('');
    setFetched(false);
    setLoading(true);
    setIsExpanded(true);
    activePromotionsGet(accessToken, locationId)
      .then((res) => {
        setData(normalisePromotions(res.body));
        setFetched(true);
        setLoading(false);
        return null;
      })
      .catch((err) => {
        setData(null);
        setError(err?.message || 'Failed to fetch active promotions.');
        setFetched(true);
        setLoading(false);
      });
  }, [accessToken, locationId]);

  return (
    <section className={`sdv-card ${isExpanded ? '' : 'sdv-card-collapsed'}`}>
      <header className="sdv-card-header">
        <button
          aria-expanded={isExpanded}
          className="sdv-card-toggle"
          type="button"
          onClick={toggleExpanded}
        >
          <span aria-hidden="true" className={`sdv-card-chevron ${isExpanded ? 'sdv-card-chevron-open' : ''}`}>▸</span>
          <h2 className="sdv-card-title">Promotions</h2>
        </button>
        <ButtonBlack
          isDisabled={!hasStore || loading}
          label={loading ? 'Getting...' : 'Get'}
          onClick={handleFetch}
        />
      </header>
      {isExpanded && (
        <div className="sdv-card-body">
          {error && (
            <p className="sdv-status-message sdv-status-error">{error}</p>
          )}
          {loading && <Loading />}
          {!loading && fetched && data && data.length === 0 && (
            <p className="sdv-card-hint">No active promotions found for this store.</p>
          )}
          {!loading && data && data.length > 0 && (
            <div className="sdv-table-wrapper">
              <p className="sdv-table-caption">{`${data.length} active promotion(s) found.`}</p>
              <table className="sdv-table">
                <thead>
                  <tr>
                    {PROMOTION_COLUMNS.map((col) => (
                      <th key={col.key}>{col.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.map((row) => (
                    <tr key={row.rowId}>
                      {PROMOTION_COLUMNS.map((col) => (
                        <td key={col.key}>{renderPromotionCell(row, col)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

PromotionTab.defaultProps = {
  selectedStore: null,
};

PromotionTab.propTypes = {
  accessToken: PropTypes.string.isRequired,
  selectedStore: PropTypes.shape({
    id: PropTypes.string,
  }),
};

export default PromotionTab;
